import { useState, useEffect, useCallback, useRef } from "react";

const XRPL_RPC = "https://xrplcluster.com/";
const DROPS_PER_XRP = 1_000_000;

export const hexEncodeUtf8 = (str) =>
  Array.from(new TextEncoder().encode(str))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

function dropsToXrp(drops) {
  const xrp = Number(drops) / DROPS_PER_XRP;
  return xrp.toFixed(6).replace(/\.?0+$/, "") || "0";
}

// ─── Provider detectors ───────────────────────────────────────────────────────

function getBescWalletProvider() {
  if (typeof window === "undefined") return null;
  if (window.xrpl?.isBescWallet) return window.xrpl;
  if (window.bescXrpl?.isBescWallet) return window.bescXrpl;
  return null;
}

// ─── Balance fetch ────────────────────────────────────────────────────────────

const fetchXrpBalance = async (address) => {
  try {
    const res = await fetch(XRPL_RPC, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        method: "account_info",
        params: [{ account: address, ledger_index: "current" }],
      }),
    });
    const data = await res.json();
    return BigInt(data.result?.account_data?.Balance ?? "0");
  } catch {
    return null;
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useXrpWallet = ({ enabled = false } = {}) => {
  const [gemAvailable, setGemAvailable]               = useState(false);
  const [gemConnected, setGemConnected]               = useState(false);
  const [gemAddress, setGemAddress]                   = useState("");

  const [xamanConnected, setXamanConnected]           = useState(false);
  const [xamanAddress, setXamanAddress]               = useState("");
  const xamanSdkRef                                   = useRef(null);
  const xamanInstanceRef                              = useRef(null);

  const [bescWalletAvailable, setBescWalletAvailable] = useState(false);
  const [bescWalletConnected, setBescWalletConnected] = useState(false);
  const [bescWalletAddress, setBescWalletAddress]     = useState("");

  const [xrpBalanceDrops, setXrpBalanceDrops]         = useState(null);
  const balancePollRef                                = useRef(null);

  const activeAddress =
    gemConnected        ? gemAddress :
    xamanConnected      ? xamanAddress :
    bescWalletConnected ? bescWalletAddress :
    "";

  // ── Detect injected wallets ───────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      setGemAvailable(false);
      setBescWalletAvailable(false);
      return;
    }

    const detect = () => setBescWalletAvailable(!!getBescWalletProvider());

    detect();
    const t1 = setTimeout(detect, 500);
    const t2 = setTimeout(detect, 1500);

    import("@gemwallet/api")
      .then(({ isInstalled }) => isInstalled())
      .then((res) => setGemAvailable(!!res?.result?.isInstalled))
      .catch(() => setGemAvailable(false));

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [enabled]);

  // ── Balance polling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !activeAddress) {
      setXrpBalanceDrops(null);
      if (balancePollRef.current) clearInterval(balancePollRef.current);
      return;
    }
    fetchXrpBalance(activeAddress).then(setXrpBalanceDrops);
    balancePollRef.current = setInterval(
      () => fetchXrpBalance(activeAddress).then(setXrpBalanceDrops),
      15_000
    );
    return () => { if (balancePollRef.current) clearInterval(balancePollRef.current); };
  }, [enabled, activeAddress]);

  // ── Reset on disable ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      setGemConnected(false);        setGemAddress("");
      setXamanConnected(false);      setXamanAddress("");
      setBescWalletConnected(false); setBescWalletAddress("");
      xamanSdkRef.current = null;
      xamanInstanceRef.current = null;
    }
  }, [enabled]);

  // ── GemWallet ─────────────────────────────────────────────────────────────
  const connectGemWallet = useCallback(async () => {
    const { getAddress } = await import("@gemwallet/api");
    const res = await getAddress();
    const addr = res?.result?.address;
    if (!addr) throw new Error("GemWallet did not return an address. Make sure it is unlocked.");
    setGemConnected(true);
    setGemAddress(addr);
    return addr;
  }, []);

  const disconnectGemWallet = useCallback(() => {
    setGemConnected(false);
    setGemAddress("");
  }, []);

  // ── Xaman ─────────────────────────────────────────────────────────────────
  const connectXaman = useCallback(async () => {
    const apiKey = process.env.REACT_APP_XAMAN_API_KEY;
    if (!apiKey) throw new Error("Xaman API key is not configured.");
    const { XummPkce } = await import("xumm-oauth2-pkce");
    const instance = new XummPkce(apiKey, { redirectUrl: window.location.origin });
    xamanInstanceRef.current = instance;
    const resolved = await instance.authorize();
    const account = resolved?.me?.account;
    if (!account) throw new Error("Xaman did not return an account address.");
    xamanSdkRef.current = resolved.sdk;
    setXamanConnected(true);
    setXamanAddress(account);
    return account;
  }, []);

  const disconnectXaman = useCallback(() => {
    try { xamanInstanceRef.current?.logout?.(); } catch { /* no-op */ }
    xamanSdkRef.current = null;
    xamanInstanceRef.current = null;
    setXamanConnected(false);
    setXamanAddress("");
  }, []);

  // ── BESC Wallet ───────────────────────────────────────────────────────────
  const connectBescWallet = useCallback(async () => {
    const provider = getBescWalletProvider();
    if (!provider) throw new Error("BESC Wallet not detected. Open this page inside the BESC Wallet browser.");
    const account = await provider.connect();
    const addr = account?.address;
    if (!addr) throw new Error("BESC Wallet did not return an address.");
    setBescWalletConnected(true);
    setBescWalletAddress(addr);
    return addr;
  }, []);

  const disconnectBescWallet = useCallback(() => {
    setBescWalletConnected(false);
    setBescWalletAddress("");
  }, []);

  // ── Send implementations ──────────────────────────────────────────────────

  const sendViaGemWallet = useCallback(async ({ drops, destination, bescAddress }) => {
    const { sendPayment } = await import("@gemwallet/api");
    const res = await sendPayment({
      amount: drops.toString(),
      destination,
      memos: [{
        memo: {
          memoData: hexEncodeUtf8(bescAddress),
          memoType: hexEncodeUtf8("besc-dest"),
        },
      }],
    });
    const hash = res?.result?.hash;
    if (!hash) throw new Error("GemWallet did not return a transaction hash.");
    return hash.toLowerCase();
  }, []);

  const sendViaXaman = useCallback(async ({ drops, destination, bescAddress, onPayloadReady }) => {
    const sdk = xamanSdkRef.current;
    if (!sdk) throw new Error("Xaman is not connected.");
    const { created, resolved: paymentResolved } = await sdk.payload.createAndSubscribe(
      {
        txjson: {
          TransactionType: "Payment",
          Amount: drops.toString(),
          Destination: destination,
          Memos: [{
            Memo: {
              MemoData: hexEncodeUtf8(bescAddress),
              MemoType: hexEncodeUtf8("besc-dest"),
            },
          }],
        },
      },
      (event) => { if ("signed" in event.data) return event.data; }
    );
    if (onPayloadReady) onPayloadReady(created?.next?.always || null);
    const result = await paymentResolved;
    if (!result?.signed) throw new Error("Xaman payment was rejected.");
    if (!result.txid) throw new Error("Xaman payment did not return a transaction ID.");
    return result.txid.toLowerCase();
  }, []);

  const sendViaBescWallet = useCallback(async ({ drops, destination, bescAddress }) => {
    const provider = getBescWalletProvider();
    if (!provider) throw new Error("BESC Wallet provider not available.");
    const result = await provider.submitPayment({
      destination,
      amount: dropsToXrp(drops),
      currency: "XRP",
      memos: [{
        memoData: hexEncodeUtf8(bescAddress),
        memoType: hexEncodeUtf8("besc-dest"),
      }],
    });
    const hash = result?.hash;
    if (!hash) throw new Error("BESC Wallet did not return a transaction hash.");
    return hash.toLowerCase();
  }, []);

  // ── Unified send / disconnect ─────────────────────────────────────────────
  const send = useCallback(
    async (params) => {
      if (gemConnected)        return sendViaGemWallet(params);
      if (xamanConnected)      return sendViaXaman(params);
      if (bescWalletConnected) return sendViaBescWallet(params);
      throw new Error("No XRP wallet connected.");
    },
    [gemConnected, xamanConnected, bescWalletConnected,
     sendViaGemWallet, sendViaXaman, sendViaBescWallet]
  );

  const disconnect = useCallback(() => {
    if (gemConnected)        disconnectGemWallet();
    if (xamanConnected)      disconnectXaman();
    if (bescWalletConnected) disconnectBescWallet();
  }, [gemConnected, xamanConnected, bescWalletConnected,
      disconnectGemWallet, disconnectXaman, disconnectBescWallet]);

  const formattedBalance =
    xrpBalanceDrops !== null
      ? (Number(xrpBalanceDrops) / DROPS_PER_XRP).toLocaleString("en-US", { maximumFractionDigits: 6 })
      : null;

  const walletType =
    gemConnected        ? "gemwallet" :
    xamanConnected      ? "xaman" :
    bescWalletConnected ? "bescwallet" :
    null;

  return {
    gemAvailable,
    xamanAvailable: !!process.env.REACT_APP_XAMAN_API_KEY,
    bescWalletAvailable,
    connected: gemConnected || xamanConnected || bescWalletConnected,
    address: activeAddress,
    walletType,
    balanceDrops: xrpBalanceDrops,
    formattedBalance,
    connectGemWallet,
    connectXaman,
    connectBescWallet,
    disconnect,
    send,
  };
};
